import { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  ChakraVisuallyHidden,
  CloseIcon,
  Column,
  EmptyState,
  Heading,
  IconButton,
  motionDurations,
  motionEasings,
  MotionBox,
  Portal,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";
import { format } from "date-fns";
import { AnimatePresence } from "motion/react";
import { useQueryClient } from "react-query";
import { validate as validateUUID } from "uuid";

import { MarkdownEditor } from "src/components/markdown-editor";
import { useHeaderHeight } from "src/contexts/header-height-context";
import {
  type AmpBoardTaskQuery,
  type AmpTaskRowFragment,
  useAmpBoardTaskQuery,
} from "src/graphql";
import {
  findTask,
  taskDetailPlaceholder,
} from "src/pages/projects/board-cache";
import { TaskLink } from "src/pages/projects/components/board-links";
import { Attachments } from "src/pages/projects/components/attachments";
import { DetailSectionSkeleton } from "src/pages/projects/components/detail-layout";
import { OverdueIcon } from "src/pages/projects/components/overdue-icon";
import { AvatarStack } from "src/pages/projects/components/people";
import { ProjectIcon } from "src/pages/projects/components/project-icon";
import {
  DetailSection,
  PropertyRow,
} from "src/pages/projects/components/property-row";
import { StatusIcon } from "src/pages/projects/components/status-icon";
import { SubtaskProgress } from "src/pages/projects/components/subtasks";
import {
  LOAD_ERROR_MESSAGE,
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
  TASK_PEOPLE_LABELS,
} from "src/pages/projects/constants";
import { useProjectTabOriginState } from "src/pages/projects/projects/project-tab-origin";
import {
  focusedPeekTaskId,
  followedPeekTaskId,
  hoverPeekTaskId,
  isPeekKeyIgnored,
  nextPeek,
  PEEK_HOVER_DWELL_MS,
  peekKey,
  peekTargetTaskId,
  peekTaskIdOf,
} from "src/pages/projects/task-peek-keys";
import {
  isOverdue,
  STATUS_CATEGORY_LABELS,
  TASK_STATE_LABELS,
  taskCategory,
} from "src/pages/projects/utils";
import { LinkButton, useLocation, useParams } from "src/router";

const PANEL_GAP = "var(--chakra-space-2)";
const SKELETON_PROPERTIES = ["a", "b", "c"];

type Peek = { taskId: string; pathname: string };

/**
 * A read-only preview of a task beside the page. Space over a task's row or
 * card opens it. While open, it follows the mouse or keyboard focus to another
 * task, and Space elsewhere, Escape, or a click outside closes it.
 */
export const TaskPeek = () => {
  const { pathname } = useLocation();
  const { headerHeight } = useHeaderHeight();
  const [peek, setPeek] = useState<Peek | null>(null);
  // Opened on one page, the preview closes when the page changes, as when its task opens in full, and stays closed on the way back.
  const openTaskId = peek?.pathname === pathname ? peek.taskId : null;
  if (peek && !openTaskId) setPeek(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Where focus was when the preview opened, to return it there if the preview closes with focus inside.
  const openerRef = useRef<HTMLElement | null>(null);
  const close = () => {
    if (panelRef.current?.contains(document.activeElement)) {
      openerRef.current?.focus();
    }
    setPeek(null);
  };
  const closeRef = useRef(close);
  closeRef.current = close;
  // The key listener lives for the whole area, so it reads the latest state through a ref.
  const latest = useRef({ openTaskId, pathname });
  latest.current = { openTaskId, pathname };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const key = peekKey(event);
      if (!key || isPeekKeyIgnored(event)) return;
      const targetTaskId = key === "space" ? peekTargetTaskId(document) : null;
      const { openTaskId: current } = latest.current;
      // A held Space would flicker the preview open and shut, so only its first press counts.
      if (event.repeat) {
        if (key === "space" && (current || targetTaskId)) {
          event.preventDefault();
        }
        return;
      }
      const next = nextPeek(key, { openTaskId: current, targetTaskId });
      if (!next.isHandled) return;
      // Stops the page scrolling, and keeps a view's own Escape, which clears its selection, for the next press.
      event.preventDefault();
      if (!next.taskId) {
        closeRef.current();
        return;
      }
      if (!current) {
        openerRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
      }
      setPeek({ taskId: next.taskId, pathname: latest.current.pathname });
    };
    // Captured, so the preview takes Space and Escape before the views' own shortcuts see them.
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  useEffect(() => {
    if (!openTaskId) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setPeek(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [openTaskId]);

  useEffect(() => {
    if (!openTaskId) return;
    let pending: { taskId: string; timer: number } | null = null;
    const cancelPending = () => {
      if (pending) window.clearTimeout(pending.timer);
      pending = null;
    };
    const show = (taskId: string) =>
      setPeek({ taskId, pathname: latest.current.pathname });

    // One delegated listener, so rows neither listen nor re-render on hover.
    const onPointerOver = (event: PointerEvent) => {
      const target = event.target;
      const taskId = hoverPeekTaskId({
        openTaskId,
        hoveredTaskId: peekTaskIdOf(target),
        pointerType: event.pointerType,
        isOverPanel:
          target instanceof Node &&
          (panelRef.current?.contains(target) ?? false),
      });
      // Moving between a row's own children keeps its dwell running.
      if (taskId !== null && taskId === pending?.taskId) return;
      cancelPending();
      if (taskId) {
        pending = {
          taskId,
          timer: window.setTimeout(() => show(taskId), PEEK_HOVER_DWELL_MS),
        };
      }
    };
    const onPointerOut = (event: PointerEvent) => {
      // Leaving the window fires no pointerover, so a row the pointer left can't switch the preview.
      if (event.relatedTarget === null) cancelPending();
    };
    const onFocusIn = (event: FocusEvent) => {
      const taskId = followedPeekTaskId({
        openTaskId,
        targetTaskId: focusedPeekTaskId(
          event.target instanceof Element ? event.target : null,
        ),
      });
      if (!taskId) return;
      cancelPending();
      show(taskId);
    };

    document.addEventListener("pointerover", onPointerOver);
    document.addEventListener("pointerout", onPointerOut);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      cancelPending();
      document.removeEventListener("pointerover", onPointerOver);
      document.removeEventListener("pointerout", onPointerOut);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [openTaskId]);

  return (
    <Portal>
      {/* The panel sits outside the page's tab order, so a screen reader hears that it opened and how to leave it. */}
      <ChakraVisuallyHidden role="status" aria-atomic="true">
        {openTaskId
          ? `${TERMS.task.Singular} preview open. Press Escape to close it.`
          : ""}
      </ChakraVisuallyHidden>
      <AnimatePresence initial={false}>
        {openTaskId && (
          <MotionBox
            key="task-peek"
            ref={panelRef}
            position="fixed"
            top={`calc(${headerHeight}px + ${PANEL_GAP})`}
            right={2}
            bottom={2}
            width="md"
            maxWidth={`calc(100vw - 2 * ${PANEL_GAP})`}
            zIndex="docked"
            display="flex"
            bg="white"
            borderRadius="xl"
            borderWidth="hairline"
            borderColor="base.border"
            boxShadow="lg"
            overflow="hidden"
            initial={{ x: 24, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{
              x: 24,
              opacity: 0,
              transition: {
                duration: motionDurations.fast,
                ease: motionEasings.easeOutCubic,
              },
            }}
            transition={{
              duration: motionDurations.normal,
              ease: motionEasings.easeOutCubic,
            }}
          >
            <TaskPeekPanel
              // Each task starts the panel's scroll from the top, and swaps in without animation as the preview follows the pointer.
              key={openTaskId}
              taskId={openTaskId}
              onClose={close}
            />
          </MotionBox>
        )}
      </AnimatePresence>
    </Portal>
  );
};

type PanelProps = {
  taskId: string;
  onClose: () => void;
};

const TaskPeekPanel = ({ taskId, onClose }: PanelProps) => {
  const { projectId: scopeProjectId } = useParams<{ projectId?: string }>();
  const originState = useProjectTabOriginState();
  const queryClient = useQueryClient();
  // The row the task was peeked from draws the panel while the full task loads.
  const placeholderData = useCallback(() => {
    const row = findTask(queryClient, taskId);
    return row ? taskDetailPlaceholder(row) : undefined;
  }, [queryClient, taskId]);
  const isTaskId = validateUUID(taskId);
  const { data, isLoading, isPlaceholderData, error } = useAmpBoardTaskQuery(
    { id: taskId },
    { enabled: isTaskId, placeholderData },
  );
  const task = data?.amp_tasks_by_pk;
  // Inside a project, the full task opens under it, as the task's links do.
  const taskPath = scopeProjectId
    ? PROJECTS_PATHS.projectTask(scopeProjectId, taskId)
    : PROJECTS_PATHS.task(taskId);
  const noun = task?.parent_task_id ? TERMS.subtask : TERMS.task;

  return (
    <Column
      as="aside"
      aria-label={`${noun.Singular} preview`}
      flex={1}
      minWidth={0}
      minHeight={0}
    >
      <Row
        align="center"
        gap={2}
        px={4}
        height={12}
        flexShrink={0}
        borderBottomWidth="hairline"
        borderColor="base.divider"
      >
        <Row align="center" gap={1.5} flex={1} minWidth={0}>
          {task?.project && (
            <>
              <ProjectIcon icon={task.project.icon} size={14} />
              <TextWithTooltip size="sm" color="text.secondary">
                {task.project.title}
              </TextWithTooltip>
            </>
          )}
        </Row>
        {task && (
          <LinkButton href={taskPath} state={originState} size="sm">
            Open {noun.singular}
          </LinkButton>
        )}
        <IconButton
          aria-label="Close preview"
          icon={CloseIcon}
          size="sm"
          variant="tertiary"
          onClick={onClose}
        />
      </Row>
      {isLoading && isTaskId ? (
        <PeekSkeleton />
      ) : task ? (
        <TaskPeekBody
          task={task}
          links={[...task.links, ...(data?.subtask_links ?? [])]}
          isPartial={isPlaceholderData}
        />
      ) : (
        <EmptyState
          variant="minimal"
          my={10}
          title={error ? undefined : `${TERMS.task.Singular} not found`}
          message={
            error
              ? LOAD_ERROR_MESSAGE
              : `Close the preview or pick another ${TERMS.task.singular}.`
          }
        />
      )}
      <Row
        flexShrink={0}
        px={4}
        py={2}
        borderTopWidth="hairline"
        borderColor="base.divider"
      >
        <Text size="sm" color="text.tertiary">
          Press Space or Esc to close
        </Text>
      </Row>
    </Column>
  );
};

type FullTask = NonNullable<AmpBoardTaskQuery["amp_tasks_by_pk"]>;

type BodyProps = {
  task: FullTask;
  links: FullTask["links"];
  /** Drawn from the task's list row, without its subtasks and links yet. */
  isPartial: boolean;
};

const NO_CHANGE = () => undefined;

const TaskPeekBody = ({ task, links, isPartial }: BodyProps) => {
  const isSubtask = task.parent_task_id !== null;
  // The row counts its subtasks, so a task with none skips that section before the full task arrives.
  const subtaskCount = task.subtasks_total.aggregate?.count ?? 0;
  const doneCount = task.subtasks.filter(
    (subtask) => taskCategory(subtask) === "done",
  ).length;
  const assignees = task.assignees.flatMap((assignee) =>
    assignee.user ? [assignee.user] : [],
  );
  const overdue = isOverdue(task);

  return (
    <Column flex={1} minHeight={0} overflowY="auto" gap={6} px={5} py={5}>
      <Column gap={2}>
        {task.parent && (
          <Text size="sm" color="text.secondary">
            {`${TERMS.subtask.Singular} of ${task.parent.title}`}
          </Text>
        )}
        <Heading size="lg">{task.title}</Heading>
      </Column>

      <Column gap={1}>
        <PropertyRow label="Status">
          <Row align="center" gap={1.5}>
            <TaskStatusIcon task={task} />
            <Text size="sm">{statusLabel(task)}</Text>
          </Row>
        </PropertyRow>
        <PropertyRow label={TASK_PEOPLE_LABELS.assignee.label}>
          {assignees.length > 0 ? (
            <Row align="center" gap={2} minWidth={0}>
              <AvatarStack people={assignees} max={3} />
              <TextWithTooltip size="sm">
                {assignees.map((person) => person.name).join(", ")}
              </TextWithTooltip>
            </Row>
          ) : (
            <Text size="sm" color="text.tertiary">
              {TASK_PEOPLE_LABELS.assignee.emptyLabel}
            </Text>
          )}
        </PropertyRow>
        <PropertyRow label="Due date">
          {task.due_at ? (
            <Row align="center" gap={1}>
              {overdue && <OverdueIcon />}
              <Text size="sm" color={overdue ? "text.danger" : undefined}>
                {format(new Date(task.due_at), "MMM d, yyyy")}
              </Text>
            </Row>
          ) : (
            <Text size="sm" color="text.tertiary">
              No due date
            </Text>
          )}
        </PropertyRow>
      </Column>

      <DetailSection title="Description">
        {task.description ? (
          <MarkdownEditor
            // The editor is uncontrolled, so a newer description remounts it.
            key={task.description}
            initialValue={task.description}
            isInline
            isReadOnly
            onChange={NO_CHANGE}
          />
        ) : (
          <Text size="sm" color="text.tertiary">
            No description
          </Text>
        )}
      </DetailSection>

      {!isSubtask && subtaskCount > 0 && (
        <DetailSection title={TERMS.subtask.Plural}>
          {isPartial ? (
            <DetailSectionSkeleton />
          ) : (
            <Column gap={2}>
              <SubtaskProgress done={doneCount} total={task.subtasks.length} />
              <Column>
                {task.subtasks.map((subtask) => (
                  <PeekSubtaskRow key={subtask.id} task={subtask} />
                ))}
              </Column>
            </Column>
          )}
        </DetailSection>
      )}

      {isPartial ? (
        <DetailSection title="Attachments">
          <DetailSectionSkeleton />
        </DetailSection>
      ) : (
        links.length > 0 && (
          <DetailSection title="Attachments">
            <Attachments links={links} isReadOnly />
          </DetailSection>
        )
      )}
    </Column>
  );
};

/** A task's workflow status, a subtask's fixed one, or archived ahead of either. */
function statusLabel(task: AmpTaskRowFragment): string {
  if (task.deleted_at) return TASK_STATE_LABELS.archived;
  return task.status?.name ?? STATUS_CATEGORY_LABELS[taskCategory(task)];
}

const TaskStatusIcon = ({ task }: { task: AmpTaskRowFragment }) => (
  <StatusIcon
    status={task.status}
    category={
      task.deleted_at
        ? "archived"
        : task.status
          ? undefined
          : taskCategory(task)
    }
  />
);

const PeekSubtaskRow = ({ task }: { task: AmpTaskRowFragment }) => {
  return (
    <TaskLink
      taskId={task.id}
      display="flex"
      alignItems="center"
      gap={2}
      minHeight={8}
      px={1.5}
      mx={-1.5}
      borderRadius="md"
      _hover={{ bg: "base.lightBackground" }}
      _focusVisible={{ boxShadow: "outline", outline: "none" }}
    >
      <TaskStatusIcon task={task} />
      <Box flex={1} minWidth={0}>
        <TextWithTooltip size="sm">{task.title}</TextWithTooltip>
      </Box>
      {task.due_at && (
        <Box flexShrink={0} ml="auto">
          <Text size="sm" color="text.tertiary">
            {format(new Date(task.due_at), "MMM d")}
          </Text>
        </Box>
      )}
    </TaskLink>
  );
};

/** The panel's bones, for a task whose row isn't cached. */
const PeekSkeleton = () => (
  <Skeleton isLoading>
    <Column gap={6} px={5} py={5}>
      <SkeletonBox height={7} width="70%" borderRadius="md" />
      <Column gap={3}>
        {SKELETON_PROPERTIES.map((key) => (
          <Row key={key} gap={2} align="center" height={8}>
            <SkeletonBox width={20} height={3} borderRadius="sm" />
            <SkeletonBox flex={1} height={5} borderRadius="md" />
          </Row>
        ))}
      </Column>
      <SkeletonBox height={16} borderRadius="lg" />
    </Column>
  </Skeleton>
);
